import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, AlertCircle, Plus, Trash2, GripVertical } from 'lucide-react'
import { RichTextEditor } from '../components/RichTextEditor'
import { fetchTemplate, createTemplate, updateTemplate } from '../utils/api'
import type { FieldDefinition } from '../types'

const CATEGORIES = ['Automóvel', 'Habitação', 'Vida', 'Saúde', 'Outros']
const FIELD_TYPES: { value: FieldDefinition['type']; label: string }[] = [
  { value: 'text', label: 'Texto' },
  { value: 'date', label: 'Data' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Telefone' },
  { value: 'textarea', label: 'Texto Longo' },
]

export function TemplateEditorPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditing = Boolean(id)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('Automóvel')
  const [content, setContent] = useState('')
  const [fields, setFields] = useState<FieldDefinition[]>([])
  const [loading, setLoading] = useState(isEditing)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      fetchTemplate(id)
        .then((template) => {
          setName(template.name)
          setDescription(template.description || '')
          setCategory(template.category || 'Automóvel')
          setContent(template.content)
          setFields(template.fields || [])
        })
        .catch(() => setError('Erro ao carregar o modelo'))
        .finally(() => setLoading(false))
    }
  }, [id])

  const extractPlaceholders = (html: string): string[] => {
    const regex = /\{\{(\w+)\}\}/g
    const matches = new Set<string>()
    let match
    while ((match = regex.exec(html)) !== null) {
      matches.add(match[1])
    }
    return Array.from(matches)
  }

  const addField = () => {
    setFields([...fields, { name: '', label: '', type: 'text', required: true }])
  }

  const updateField = (index: number, updates: Partial<FieldDefinition>) => {
    setFields(fields.map((f, i) => (i === index ? { ...f, ...updates } : f)))
  }

  const removeField = (index: number) => {
    setFields(fields.filter((_, i) => i !== index))
  }

  const syncFieldsFromContent = () => {
    const placeholders = extractPlaceholders(content)
    const existingNames = new Set(fields.map((f) => f.name))
    const newFields = placeholders
      .filter((p) => !existingNames.has(p))
      .map((p) => ({
        name: p,
        label: p.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        type: p.includes('data') ? 'date' : p.includes('email') ? 'email' : p.includes('telefone') ? 'tel' : 'text',
        required: true,
      } as FieldDefinition))
    if (newFields.length > 0) {
      setFields([...fields, ...newFields])
    }
  }

  const handleSave = async () => {
    if (!name.trim()) {
      setError('O nome do modelo é obrigatório')
      return
    }
    if (!content.trim()) {
      setError('O conteúdo do modelo é obrigatório')
      return
    }
    if (fields.length === 0) {
      setError('Adicione pelo menos um campo ao formulário')
      return
    }
    if (fields.some((f) => !f.name.trim() || !f.label.trim())) {
      setError('Todos os campos devem ter nome e etiqueta')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const templateData = {
        name: name.trim(),
        description: description.trim() || null,
        category,
        content,
        fields,
      }

      if (isEditing && id) {
        await updateTemplate(id, templateData)
      } else {
        await createTemplate(templateData)
      }

      navigate('/admin')
    } catch {
      setError('Erro ao guardar o modelo')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="flex-1 py-6 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Editar Modelo' : 'Novo Modelo'}
          </h1>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="card p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="label">
                Nome do Modelo *
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="Ex: Resolução Automóvel - Venda"
              />
            </div>

            <div>
              <label htmlFor="category" className="label">
                Categoria/Ramo *
              </label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="input"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="description" className="label">
              Descrição (opcional)
            </label>
            <input
              type="text"
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input"
              placeholder="Ex: Resolução por venda do veículo"
            />
          </div>

          <div>
            <label className="label">Conteúdo do Documento *</label>
            <p className="text-sm text-gray-500 mb-3">
              Use placeholders como <code className="bg-gray-100 px-1 rounded">{'{{nome}}'}</code> para campos dinâmicos.
              Adicione <code className="bg-gray-100 px-1 rounded">[SIGNATURE_PLACEHOLDER]</code> onde a assinatura deve aparecer.
            </p>
            <RichTextEditor
              content={content}
              onChange={setContent}
              placeholder="Comece a escrever o conteúdo do documento..."
            />
          </div>

          {content && extractPlaceholders(content).length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-blue-900">
                  Placeholders detectados no documento:
                </h3>
                <button
                  type="button"
                  onClick={syncFieldsFromContent}
                  className="text-xs px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Sincronizar Campos
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {extractPlaceholders(content).map((p) => (
                  <span
                    key={p}
                    className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-md font-mono"
                  >
                    {`{{${p}}}`}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-900">Campos do Formulário</h2>
              <p className="text-sm text-gray-500">
                Defina os campos que o cliente deve preencher e se são obrigatórios
              </p>
            </div>
            <button
              type="button"
              onClick={addField}
              className="btn-secondary gap-2"
            >
              <Plus className="w-4 h-4" />
              Adicionar Campo
            </button>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
            <p className="font-medium text-amber-800 mb-1">💡 Campos com autocomplete:</p>
            <p className="text-amber-700">
              Use o nome <code className="bg-amber-100 px-1 rounded">seguradora</code> para ativar autocomplete com lista de seguradoras portuguesas.
              Campos com <code className="bg-amber-100 px-1 rounded">morada_seguradora</code> e <code className="bg-amber-100 px-1 rounded">nif_seguradora</code> serão preenchidos automaticamente.
            </p>
          </div>

          {fields.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <GripVertical className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Nenhum campo definido.</p>
              <p className="text-sm">Clique em "Sincronizar Campos" acima ou adicione manualmente.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {fields.map((field, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl"
                >
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-500">Nome (placeholder)</label>
                      <input
                        type="text"
                        value={field.name}
                        onChange={(e) => updateField(index, { name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                        className="input text-sm mt-1"
                        placeholder="nome_campo"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Etiqueta (visível)</label>
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => updateField(index, { label: e.target.value })}
                        className="input text-sm mt-1"
                        placeholder="Nome do Campo"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Tipo</label>
                      <select
                        value={field.type}
                        onChange={(e) => updateField(index, { type: e.target.value as FieldDefinition['type'] })}
                        className="input text-sm mt-1"
                      >
                        {FIELD_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => updateField(index, { required: e.target.checked })}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">Obrigatório</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => removeField(index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => navigate('/admin')}
            className="btn-secondary flex-1"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex-1 gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                A guardar...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Guardar Modelo
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
